'use client';

import { useState, useEffect, useMemo, JSX } from 'react';
import { useDebounce } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { Tabs, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';
import { ContentEditSegment, Voice } from '../types';
import Dropdown from './Dropdown';
import GenerateButton from './GenerateButton';
import VoiceCard from './VoiceCard';

const MAX_INPUT_LENGTH = 4500;

export type TextInputProps = {
    onSubmit: () => void;
    buttonText: string;
    voiceId: Voice['voice_id'] | null;
    script: string;
    speed: number;
    calculatedCost: number;
};

const TextInput = (props: TextInputProps): JSX.Element => {
    const { onSubmit, voiceId, script = '', speed, calculatedCost = 0 } = props;
    const { updateState, textToSpeech, activeTab } = useVoiceoverTabContext();
    const [inputError, setInputError] = useState<string | undefined>(undefined);
    const {
        contentEditSegment,
        credits,
        voices,
        CreditsApi: { calculateCost },
    } = useVoiceoverContext();
    const selectedVoice = voices.find((voice) => voice.voice_id === textToSpeech?.selectedVoice);

    const buttonText = useMemo(() => {
        const preCannedText = (
            {
                [ContentEditSegment.Unsubscribed]: 'Subscribe to Generate',
                [ContentEditSegment.UpgradableUser]: 'Upgrade to Generate',
                [ContentEditSegment.Disabled]: 'Disabled',
            } as Partial<Record<ContentEditSegment, string>>
        )[contentEditSegment];

        if (preCannedText) {
            return preCannedText;
        }

        if (credits === 0) return 'Purchase Credits';

        return props.buttonText;
    }, [contentEditSegment, credits, props.buttonText]);

    useDebounce(
        async () => {
            if (!script || script.length === 0 || !voiceId) return 0;

            try {
                const cost = await calculateCost(script);
                updateState({ calculatedCost: Math.abs(cost) });
            } catch (err) {
                console.error('calculateCost failed:', err);
            }
            return 0;
        },
        250,
        [script, voiceId],
    );

    useEffect(() => {
        if (script.length > MAX_INPUT_LENGTH) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInputError(
                `Your script exceeds the maximum character limit of ${MAX_INPUT_LENGTH}. Please shorten your message to continue.`,
            );
        } else {
            setInputError(undefined);
        }
    }, [script]);

    return (
        <div className="text-left">
            <div
                className={twMerge(
                    'flex flex-col bg-gray-900 rounded-lg p-2 sm:p-4 py-4 mt-4 min-h-60 max-h-[40rem]',
                    inputError && 'border-2 border-red-400',
                )}
            >
                <textarea
                    className="w-full bg-gray-900 text-white focus:outline-none placeholder:text-white flex-grow field-sizing-content min-h-24"
                    style={{ resize: 'vertical' }}
                    name="script-input"
                    data-testid="script-input"
                    placeholder="Paste or type your script here..."
                    value={script}
                    onChange={(e) => updateState({ script: e.target.value })}
                />
                <div className="flex flex-col md:flex-row justify-between items-end mt-4 gap-x-2">
                    <div className="flex flex-row gap-2 md:gap-6">
                        {activeTab === Tabs.TextToSpeech && (
                            <div className="flex flex-col gap-1">
                                <Text fontWeight="regular" fontSize="sm">
                                    Selected Voice
                                </Text>
                                {selectedVoice && (
                                    <VoiceCard
                                        voice={selectedVoice}
                                        selectedVoice={selectedVoice.voice_id}
                                        setSelectedVoice={(selectedVoice) => updateState({ selectedVoice })}
                                        isCompact
                                    />
                                )}
                            </div>
                        )}
                        <div className="flex flex-col gap-1">
                            <Text fontWeight="regular" fontSize="sm">
                                Speech Speed
                            </Text>
                            <Dropdown
                                value={speed}
                                minValue={70} // min and max values are set based on ElevenLabs' API limits. but not written in the docs...
                                maxValue={120}
                                onChange={(speed) => updateState({ voiceoverParams: { speed } })}
                                header="Speed"
                            />
                        </div>
                    </div>
                    <div className="flex flex-row gap-2 items-end md:items-start">
                        <Text fontWeight="regular" className="mt-2">
                            {credits} {credits === 1 ? 'credit' : 'credits'} available
                        </Text>
                        <GenerateButton
                            className="mt-4 sm:mt-0"
                            disabled={!!inputError}
                            onSubmit={() => {
                                if (script.length === 0) {
                                    setInputError('Please enter a script to generate voiceover audio.');
                                    return;
                                }
                                onSubmit();
                            }}
                            calculatedCost={calculatedCost}
                        >
                            {buttonText}
                        </GenerateButton>
                    </div>
                </div>
            </div>
            <Text className="mt-2" fontWeight="regular">
                {MAX_INPUT_LENGTH - script.length > 0 && <>{MAX_INPUT_LENGTH - script.length} characters remaining</>}
            </Text>
            {!!inputError && (
                <Text fontWeight="regular" className="text-red-100 text-left mt-2">
                    {inputError}
                </Text>
            )}
        </div>
    );
};

export default TextInput;
